import React from 'react';

/**
 * The MindlyFriend mark: a line-art head in profile with a lotus inside it.
 *
 * Rebuilt as inline SVG rather than shipped as a bitmap so it stays sharp at
 * every size, costs no request, and can be recoloured per context — the two
 * strokes are driven by CSS custom properties, which is what lets the same
 * component go full-white on the dark footer and on the blue About panel.
 *
 * The viewBox is cropped to the artwork's stroke bounds rather than a loose
 * 64-unit square, so the head fills its box instead of floating in padding —
 * that is the difference between legible and a green blob at navbar size.
 *
 * @param {'brand'|'mono'} tone  'mono' paints both strokes in `currentColor`.
 */
export function LogoMark({ size = 32, tone = 'brand', className = '' }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="9 6 50 57"
      fill="none"
      strokeWidth="3"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={`mm-logo-svg mm-logo-svg--${tone} ${className}`}
      aria-hidden="true"
      focusable="false"
    >
      {/* Head in profile, facing right. Open at the base, as in the original. */}
      <path
        className="mm-logo-svg__head"
        d="M20.5 57.5c0-6.6-1.3-10.2-4.1-14.1C10.6 35.6 10.2 24.6 16 17.2 22.7 8.6 35.6 6.6 44 12.9c6 4.5 8.7 11.4 7.6 18.4-.3 1.9.1 3 1.1 4.5l2.8 4.4c1 1.5.2 3.1-1.6 3.4l-3.6.6c.1 1.2.1 2.4 0 3.6-.3 2.4-2.2 3.8-5 3.8h-3.4c-2.6 0-4.2 1.6-4.2 4.2v2.2"
      />
      {/* Lotus: one upright petal flanked by two that flare up and outward.
          The side petals rise at roughly 45°, matching the brand asset — at a
          shallower angle the flower reads as a pair of wings instead. */}
      <g className="mm-logo-svg__lotus">
        <path d="M26 14.5c4 4.6 4 14.2 0 18.8-4-4.6-4-14.2 0-18.8Z" />
        <path d="M26 34c-4.5.5-9.2-3-11.2-9.8 5.6-.6 9.6 3.2 11.2 9.8Z" />
        <path d="M26 34c4.5.5 9.2-3 11.2-9.8-5.6-.6-9.6 3.2-11.2 9.8Z" />
      </g>
    </svg>
  );
}

/**
 * Full lockup: mark plus wordmark, with an optional tagline.
 *
 * `tone="mono"` inverts the whole lockup to `currentColor` for dark surfaces.
 */
export default function Logo({
  wordmark = 'MindlyFriend',
  // The tail of the wordmark carries the brand green, tying the name to the
  // lotus in the mark. Split as a prop rather than hardcoded so the component
  // still renders any other wordmark it is handed.
  accent = 'Friend',
  tagline,
  tone = 'brand',
  markSize = 34,
  className = '',
}) {
  return (
    <span className={`mm-logo mm-logo--${tone} ${className}`}>
      <LogoMark size={markSize} tone={tone} />

      <span className="mm-logo__text">
        <span className="mm-logo__wordmark">
          {accent && wordmark.endsWith(accent) ? (
            <>
              {wordmark.slice(0, -accent.length)}
              <span className="mm-logo__wordmark-accent">{accent}</span>
            </>
          ) : (
            wordmark
          )}
        </span>
        {tagline && <span className="mm-logo__tagline">{tagline}</span>}
      </span>
    </span>
  );
}
